
import { defineComponent } from "vue";
import { suggestSocialImage } from "@/api/ghlAi.api.js";
import { getPlatformMeta } from "@/utils/socialPlatformMeta.js";

type GeneratedImage = {
  b64Json: string;
  mimeType: string;
  previewUrl: string;
};

type ReferenceFile = {
  file: File;
  previewUrl: string;
};

export default defineComponent({
  name: "suggest-post-image-dialog",
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    platforms: {
      type: Array as () => string[],
      default: () => []
    },
    initialPrompt: {
      type: String,
      default: ""
    }
  },
  emits: ["update:modelValue", "select"],
  data() {
    return {
      generating: false,
      creditsError: "",
      creditsRemaining: null as number | null,
      images: [] as GeneratedImage[],
      referenceFiles: [] as ReferenceFile[],
      variationOptions: [
        {
          count: 1,
          title: "One image",
          description: "Generate a single suggestion. Uses fewer AI credits."
        },
        {
          count: 2,
          title: "Two images",
          description: "Generate two options so you can pick the better one. Uses more AI credits."
        }
      ],
      form: {
        prompt: "",
        referenceMode: "keep_product" as "keep_product" | "match_mood",
        variationCount: 1,
        rightsConfirmed: false
      }
    };
  },
  computed: {
    visible: {
      get(): boolean {
        return this.modelValue;
      },
      set(value: boolean) {
        this.$emit("update:modelValue", value);
      }
    },
    platformLabels(): string[] {
      return (this.platforms || []).map((platform) => getPlatformMeta(platform).label);
    },
    promptPlaceholder(): string {
      if (this.referenceFiles.length) {
        return "Describe the image you want. Example: Same product as my photo on a clean white background with soft morning light. Or: Warm motivational look like my reference — do not copy the exact photo.";
      }
      return "Describe the product, scene, or mood you want in the image. Example: Wellness product on a bright kitchen counter with natural morning light.";
    },
    canGenerate(): boolean {
      if (!this.form.prompt.trim()) return false;
      if (this.referenceFiles.length && !this.form.rightsConfirmed) return false;
      return true;
    }
  },
  watch: {
    modelValue(open: boolean) {
      if (open) {
        this.resetForOpen();
      }
    }
  },
  methods: {
    resetForOpen() {
      this.revokeReferencePreviews();
      this.revokeResultPreviews();
      this.referenceFiles = [];
      this.images = [];
      this.creditsError = "";
      this.creditsRemaining = null;
      this.form.prompt = String(this.initialPrompt || "").trim();
      this.form.referenceMode = "keep_product";
      this.form.variationCount = 1;
      this.form.rightsConfirmed = false;
    },
    close() {
      this.visible = false;
    },
    onClosed() {
      this.generating = false;
      this.revokeReferencePreviews();
      this.revokeResultPreviews();
      this.referenceFiles = [];
      this.images = [];
      this.creditsError = "";
    },
    triggerReferencePicker() {
      const input = this.$refs.referenceInput as HTMLInputElement;
      input?.click();
    },
    onReferenceFilesSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const files = Array.from(input.files || []);
      for (const file of files) {
        if (this.referenceFiles.length >= 3) break;
        if (!file.type.startsWith("image/")) continue;
        this.referenceFiles.push({
          file,
          previewUrl: URL.createObjectURL(file)
        });
      }
      input.value = "";
    },
    removeReferenceFile(index: number) {
      const item = this.referenceFiles[index];
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      this.referenceFiles.splice(index, 1);
      if (!this.referenceFiles.length) {
        this.form.rightsConfirmed = false;
      }
    },
    revokeReferencePreviews() {
      for (const item of this.referenceFiles) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      }
    },
    revokeResultPreviews() {
      for (const item of this.images) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      }
    },
    async generate() {
      if (!this.canGenerate || this.generating) return;

      this.generating = true;
      this.creditsError = "";

      try {
        const formData = new FormData();
        formData.append("prompt", this.form.prompt.trim());
        formData.append("variationCount", String(this.form.variationCount));
        if (this.platforms.length) {
          formData.append("platforms", JSON.stringify(this.platforms));
        }
        if (this.referenceFiles.length) {
          formData.append("referenceMode", this.form.referenceMode);
          for (const item of this.referenceFiles) {
            formData.append("files", item.file);
          }
        }

        const response = await suggestSocialImage(formData);
        const data = response.data || response;
        const rawImages = Array.isArray(data.images) ? data.images : [];
        this.images = rawImages.map((image: { b64Json?: string; mimeType?: string }) => {
          const mimeType = String(image.mimeType || "image/png");
          const b64Json = String(image.b64Json || "");
          return {
            b64Json,
            mimeType,
            previewUrl: b64Json ? `data:${mimeType};base64,${b64Json}` : ""
          };
        }).filter((image: GeneratedImage) => image.previewUrl);
        this.creditsRemaining = Number(data.creditsRemaining) || null;

        if (!this.images.length) {
          this.creditsError = "No images were returned. Please try again.";
        }
      } catch (err) {
        const error = err as { response?: { status?: number; data?: { message?: string } }; message?: string };
        const status = error.response?.status;
        const message = error.response?.data?.message || error.message || "Unable to generate image.";
        if (status === 429 || /insufficient ai credits/i.test(message)) {
          this.creditsError = "Insufficient AI credits.";
        } else {
          this.creditsError = message;
        }
      } finally {
        this.generating = false;
      }
    },
    regenerate() {
      this.revokeResultPreviews();
      this.images = [];
      this.generate();
    },
    useImage(image: GeneratedImage) {
      this.$emit("select", {
        b64Json: image.b64Json,
        mimeType: image.mimeType
      });
      this.close();
    }
  }
});
